import { useCallback, useRef, useState } from 'react';
import { isWidgetAuthExpiredError, webchatApi } from '../../services/api';
import { readStreamSimple } from '../../services/stream';
import { t } from '../../i18n';
import { getSttService } from '../../services/stt';
import { resolveWidgetLocale } from '../../utils/locale';
import { normalizeSpokenDigitSequences } from '../../utils/spokenDigits';
import type {
  AuthState,
  ChatMessage,
  FileAttachment,
  FileUploadState,
  WidgetConfig,
  WidgetState,
} from '../../types';
import { generateId } from './helpers';
import type { ChatOrchestrator } from '../../services/orchestrator';
import { buildFileReference } from '../../utils/file-field';
import {
  appendAssistantToHistory,
  appendUserToHistory,
  seedHistoryIfEmpty,
  waitForOrchestrator,
} from '../../utils/historyMessages';
import { store } from '../../state/store';
import { ensureOrchestratorRef } from '../messageList/MessageList.helpers';

interface InputBarMessageActions {
  addMessage(message: ChatMessage): void;
  removeMessage(id: string): void;
  updateMessage(id: string, partial: Partial<ChatMessage>): void;
  setState(partial: Partial<WidgetState>): void;
  setAuth(auth: Partial<AuthState>): void;
  setError(error: string | null): void;
  setFileUpload(fileUpload: Partial<FileUploadState>): void;
  addPendingAttachment(attachment: FileAttachment): void;
  clearPendingAttachments(): FileAttachment[];
  requestInputFocus(): void;
  setDrivingMode(partial: Partial<WidgetState['drivingMode']>): void;
}

interface UseInputBarActionsArgs extends InputBarMessageActions {
  orchestratorRef: React.RefObject<ChatOrchestrator | null> | null;
  getMcpReadyPromise?: () => Promise<void> | null;
  auth: AuthState;
  config: WidgetConfig;
  isLoading: boolean;
  pendingAttachments: FileAttachment[];
  conversationId: string | null;
  clientChatId: string;
  inputRef: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  drivingListening: boolean;
  setWaveLevel(level: number): void;
  resetWaveform(immediate?: boolean): void;
}

export function useInputBarActions({
  orchestratorRef,
  getMcpReadyPromise,
  auth,
  config,
  isLoading,
  pendingAttachments,
  conversationId,
  clientChatId,
  inputRef,
  drivingListening,
  addMessage,
  removeMessage,
  updateMessage,
  setState,
  setAuth,
  setError,
  setFileUpload,
  addPendingAttachment,
  clearPendingAttachments,
  requestInputFocus,
  setDrivingMode,
  setWaveLevel,
  resetWaveform,
}: UseInputBarActionsArgs) {
  const [inputDraft, setInputDraft] = useState('');
  const activeMicSessionIdRef = useRef(0);
  const nonOrchestratorAbortRef = useRef<AbortController | null>(null);

  const addUserMessage = useCallback((content: string) => {
    addMessage({ id: generateId(), role: 'user', content, timestamp: Date.now() });
  }, [addMessage]);

  const addAssistantMessage = useCallback((content: string) => {
    addMessage({ id: generateId(), role: 'assistant', content, timestamp: Date.now() });
  }, [addMessage]);

  const addGreetingMessage = useCallback((content: string) => {
    addMessage({ id: generateId(), role: 'assistant', content, timestamp: Date.now(), isGreeting: true });
  }, [addMessage]);

  const updateChatInputValue = useCallback((value: string, focus = false) => {
    setInputDraft(value);
    if (inputRef.current) {
      inputRef.current.value = value;
      if (focus) inputRef.current.focus();
    }
  }, [inputRef]);

  const handleEmailSubmit = useCallback(async (email: string) => {
    if (!email.includes('@') || !email.includes('.')) {
      addAssistantMessage(t('authInvalidEmail'));
      return;
    }

    addUserMessage(email);
    updateChatInputValue('');
    setState({ isLoading: true });
    try {
      await webchatApi.authStart(email);
      setAuth({ status: 'otp_pending', email });
      addAssistantMessage(t('authOtpSent').replace('{email}', email));
    } catch {
      addAssistantMessage(t('authSendFailed'));
    } finally {
      setState({ isLoading: false });
    }
  }, [addAssistantMessage, addUserMessage, setAuth, setState, updateChatInputValue]);

  const handleOtpSubmit = useCallback(async (code: string) => {
    if (!/^\d{4,6}$/.test(code)) {
      addAssistantMessage(t('authInvalidOtp'));
      return;
    }

    addUserMessage(code);
    updateChatInputValue('');
    setState({ isLoading: true });
    try {
      const result = await webchatApi.authVerify(auth.email!, code);
      setAuth({ status: 'authenticated', email: auth.email, token: result.token ?? null });
      addGreetingMessage(config.greeting || t('authSuccess'));
    } catch {
      addAssistantMessage(t('authVerifyFailed'));
    } finally {
      setState({ isLoading: false });
    }
  }, [addAssistantMessage, addGreetingMessage, addUserMessage, auth.email, config.greeting, setAuth, setState, updateChatInputValue]);

  const handleChatMessage = useCallback(async (text: string, attachments: FileAttachment[]) => {
    if (auth.status !== 'authenticated') return;

    let content = text;
    if (attachments.length > 0) {
      const fileRefs = attachments
        .map(a => buildFileReference({
          filename: a.filename,
          storagePath: a.storagePath,
          mimeType: a.mimeType,
          originalName: a.originalName,
          sizeBytes: a.size,
          fileId: a.fileId,
          fileHandle: a.fileHandle,
        }))
        .join(' ');
      content = text ? `${fileRefs}\n${text}` : fileRefs;
    }

    const orchestrator =
      orchestratorRef?.current
      ?? await ensureOrchestratorRef()
      ?? await waitForOrchestrator(orchestratorRef, getMcpReadyPromise);
    if (orchestrator) {
      await orchestrator.sendMessage(content, attachments);
      return;
    }

    seedHistoryIfEmpty();

    const userMsg = {
      id: generateId(),
      role: 'user' as const,
      content,
      timestamp: Date.now(),
      attachments: attachments.length > 0 ? attachments : undefined,
    };
    addMessage(userMsg);
    appendUserToHistory(content);

    const assistantId = generateId();
    addMessage({ id: assistantId, role: 'assistant', content: '', timestamp: Date.now(), isStreaming: true });
    setState({ isLoading: true });

    const abortController = new AbortController();
    nonOrchestratorAbortRef.current = abortController;

    try {
      const response = await webchatApi.chat(
        store.getState().historyMessages,
        undefined,
        conversationId || undefined,
        clientChatId,
        undefined,
        undefined,
        abortController.signal,
      );

      if (!response.ok) {
        if (response.status === 429) {
          let retryAfterMs = 30_000;
          let captchaRequired = false;
          try {
            const body = await response.json();
            if (body.retryAfterMs) retryAfterMs = body.retryAfterMs;
            if (body.captchaRequired) captchaRequired = true;
          } catch {
            // use defaults
          }
          throw Object.assign(new Error(t('rateLimited') || 'Please wait.'), {
            isRateLimited: true,
            retryAfterMs,
            captchaRequired,
          });
        }
        throw new Error(`Chat failed: ${response.status}`);
      }

      await readStreamSimple(
        response,
        assistantId,
        null as unknown as ShadowRoot,
        (finalText: string) => {
          updateMessage(assistantId, { content: finalText, isStreaming: false });
          appendAssistantToHistory(finalText);
          setTimeout(() => {
            setState({ isLoading: false });
            requestInputFocus();
          }, 2000);
        },
        (err: Error) => {
          updateMessage(assistantId, {
            content: t('chatError') || 'Sorry, an error occurred.',
            isStreaming: false,
          });
          setState({ isLoading: false });
          requestInputFocus();
          setError(err.message);
        },
      );
    } catch (err) {
      const isAborted = (err instanceof Error && (err.name === 'AbortError' || err.message === 'The operation was aborted.'));
      if (isAborted) {
        // User clicked stop - message already handled by handleStop
        return;
      }
      const isRateLimited = (err as { isRateLimited?: boolean })?.isRateLimited === true;
      if (isWidgetAuthExpiredError(err)) {
        removeMessage(assistantId);
        setState({ isLoading: false });
        requestInputFocus();
        return;
      }
      updateMessage(assistantId, {
        content: isRateLimited
          ? (t('rateLimited') || 'Please wait before sending another message.')
          : (t('chatError') || 'Sorry, an error occurred.'),
        isStreaming: false,
      });

      if (isRateLimited) {
        const lockMs = (err as { retryAfterMs?: number }).retryAfterMs || 30_000;
        const captchaNeeded = (err as { captchaRequired?: boolean }).captchaRequired === true;
        setState({
          isLoading: false,
          rateLimitLock: { lockedUntil: Date.now() + lockMs, captchaRequired: captchaNeeded },
        });
      } else {
        setState({ isLoading: false });
        requestInputFocus();
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    } finally {
      nonOrchestratorAbortRef.current = null;
    }
  }, [addMessage, auth.status, clientChatId, conversationId, getMcpReadyPromise, orchestratorRef, removeMessage, requestInputFocus, setError, setState, updateMessage]);


  const handleSubmit = useCallback(async () => {
    const text = inputRef.current?.value.trim() ?? '';
    if (isLoading) return;

    const hasAttachments = pendingAttachments.length > 0;
    if (!text && !hasAttachments) return;

    if (auth.status === 'unauthenticated') {
      if (!text) return;
      await handleEmailSubmit(text);
    } else if (auth.status === 'otp_pending') {
      if (!text) return;
      await handleOtpSubmit(text);
    } else {
      updateChatInputValue('');
      if (inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.style.height = '40px';
        const shell = inputRef.current.parentElement as HTMLElement | null;
        if (shell) shell.style.height = 'auto';
      }
      const attachments = clearPendingAttachments();
      await handleChatMessage(text, attachments);
    }
  }, [
    auth.status,
    clearPendingAttachments,
    handleChatMessage,
    handleEmailSubmit,
    handleOtpSubmit,
    inputRef,
    isLoading,
    pendingAttachments.length,
    updateChatInputValue,
  ]);

  const handleSsoLogin = useCallback(() => {
    const { serverUrl, authProvider, widgetId } = config;
    const params = new URLSearchParams({ provider: authProvider });
    if (widgetId) params.set('widgetId', widgetId);
    params.set('origin', window.location.origin);
    const loginUrl = `${serverUrl}/webchat/auth/sso/login?${params.toString()}`;
    const expectedServerOrigin = (() => {
      try {
        return new URL(serverUrl).origin;
      } catch {
        return window.location.origin;
      }
    })();

    const popup = window.open(loginUrl, 'webchat-sso', 'width=500,height=600,popup=yes');
    if (!popup) {
      addAssistantMessage(t('authSsoFailed'));
      return;
    }

    let completed = false;
    setAuth({ status: 'sso_pending', email: null, token: null });

    function handleSsoResult(email: string | undefined, error: string | undefined, token: string | undefined) {
      completed = true;
      cleanup();
      if (!error) {
        setAuth({ status: 'authenticated', email: email || null, token: token ?? null });
        addGreetingMessage(config.greeting || t('authSuccess'));
      } else {
        setAuth({ status: 'unauthenticated', email: null, token: null });
        addAssistantMessage(t('authSsoFailed'));
      }
    }

    function onMessage(event: MessageEvent) {
      if (event.origin !== expectedServerOrigin) return;
      if (event.source !== popup) return;
      if (event.data?.type !== 'webchat-auth') return;
      handleSsoResult(event.data.email, event.data.error, event.data.token);
    }

    // Fallback: some IdPs (e.g. Microsoft) set window.opener = null during login,
    // breaking postMessage. localStorage fires a storage event in the parent window.
    function onStorage(e: StorageEvent) {
      if (e.key !== 'webchat-sso-complete' || !e.newValue) return;
      try {
        const data = JSON.parse(e.newValue) as { type?: string; email?: string; error?: string; token?: string; ts?: number };
        if (data.type !== 'webchat-auth') return;
        if (data.ts && Date.now() - data.ts > 30_000) return; // ignore stale entries
        try { window.localStorage.removeItem('webchat-sso-complete'); } catch { /* ignore */ }
        handleSsoResult(data.email, data.error, data.token);
      } catch { /* ignore malformed entries */ }
    }

    const pollTimer = setInterval(() => {
      if (popup.closed) {
        cleanup();
        if (!completed) {
          setAuth({ status: 'unauthenticated', email: null, token: null });
          addAssistantMessage(t('authSsoCancelled'));
        }
      }
    }, 500);

    function cleanup() {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
      clearInterval(pollTimer);
    }

    window.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);
  }, [addAssistantMessage, addGreetingMessage, config, setAuth]);

  const handleFileUpload = useCallback(async (file: File) => {
    setFileUpload({ file, uploading: true, progress: 0, error: null });
    try {
      const result = await webchatApi.upload(file);
      addPendingAttachment({
        fileId: result.fileId || result.fileHandle,
        fileHandle: result.fileHandle || result.fileId,
        filename: result.filename || result.originalName,
        originalName: result.originalName || result.filename,
        mimeType: result.mimeType || file.type,
        size: result.size || file.size,
        storagePath: result.storagePath,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      });
      setFileUpload({ file: null, uploading: false, progress: 100, error: null });
      requestInputFocus();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : (t('uploadError') || 'Upload failed');
      setFileUpload({
        file: null,
        uploading: false,
        progress: 0,
        error: message,
      });
    }
  }, [addPendingAttachment, requestInputFocus, setFileUpload]);

  const handleAttachClick = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx';
    fileInput.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      if (file) {
        void handleFileUpload(file);
      }
    });
    fileInput.click();
  }, [handleFileUpload]);

  const handleMicClick = useCallback(() => {
    const stt = getSttService();
    if (drivingListening) {
      resetWaveform();
      stt.stop();
      setDrivingMode({ listening: false, transcript: '' });
      return;
    }

    const draftBeforeRecording = inputRef.current?.value ?? '';
    const sessionId = activeMicSessionIdRef.current + 1;
    activeMicSessionIdRef.current = sessionId;
    setDrivingMode({ error: null, transcript: '', listening: true });
    requestAnimationFrame(() => setWaveLevel(0.18));
    stt.start(resolveWidgetLocale(config.language), {
      onInterim: (text) => {
        if (sessionId !== activeMicSessionIdRef.current) return;
        const prefix = draftBeforeRecording ? `${draftBeforeRecording} ` : '';
        updateChatInputValue(prefix + text);
        setDrivingMode({ transcript: text });
      },
      onAudioLevel: (level) => {
        if (sessionId !== activeMicSessionIdRef.current) return;
        setWaveLevel(level);
      },
      onFinal: (text) => {
        if (sessionId !== activeMicSessionIdRef.current) return;
        resetWaveform();
        setDrivingMode({ listening: false, transcript: '' });
        const trimmed = text.trim();
        if (!trimmed) return;
        const processed = normalizeSpokenDigitSequences(trimmed);
        const prefix = draftBeforeRecording ? `${draftBeforeRecording} ` : '';
        requestAnimationFrame(() => updateChatInputValue(prefix + processed, true));
      },
      onError: (errorKey) => {
        if (sessionId !== activeMicSessionIdRef.current) return;
        resetWaveform(true);
        setDrivingMode({ listening: false, transcript: '', error: errorKey });
        if (errorKey === 'sttApiKeyMissing') {
          addAssistantMessage(t('sttApiKeyMissing'));
          requestInputFocus();
        }
      },
      onEnd: () => {
        if (sessionId !== activeMicSessionIdRef.current) return;
        resetWaveform(true);
        setDrivingMode({ listening: false, transcript: '' });
      },
    });
  }, [
    addAssistantMessage,
    config.language,
    drivingListening,
    inputRef,
    requestInputFocus,
    resetWaveform,
    setDrivingMode,
    setWaveLevel,
    updateChatInputValue,
  ]);

const handleTextareaInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
    const element = e.currentTarget;
    const shell = element.parentElement as HTMLElement | null;

    setInputDraft(element.value);

    element.style.height = 'auto';
    const nextHeight = Math.min(element.scrollHeight + 2, 400);
    element.style.height = `${nextHeight}px`;

    if (shell) {
      shell.style.height = `${nextHeight}px`;
    }
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputDraft(e.target.value);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  }, [handleSubmit]);

  const handleStop = useCallback(() => {
    if (orchestratorRef?.current) {
      orchestratorRef.current.interruptCurrentTurn();
    } else if (nonOrchestratorAbortRef.current) {
      nonOrchestratorAbortRef.current.abort();
      nonOrchestratorAbortRef.current = null;
      setState({ isLoading: false });
      requestInputFocus();
    }
  }, [orchestratorRef, requestInputFocus, setState]);

  return {
    inputDraft,
    addAssistantMessage,
    handleSubmit,
    handleStop,
    handleSsoLogin,
    handleAttachClick,
    handleMicClick,
    handleTextareaInput,
    handleInputChange,
    handleKeyDown,
  };
}
